import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Euro, Clock, Calculator, Power } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

/**
 * AdminPricing — admin can override the base normal/promo price per typology.
 * Durations are fixed (system parameter), only prices are editable.
 */
export default function AdminPricing() {
  const [types, setTypes] = useState([]);
  const [drafts, setDrafts] = useState({});   // {T0: {normal, promo}}
  const [saving, setSaving] = useState(null); // apt_type currently saving
  const [settings, setSettings] = useState(null);
  const [togglingCalc, setTogglingCalc] = useState(false);

  const load = () =>
    api.get("/catalog/apartment-types").then(({ data }) => {
      setTypes(data.items);
      const d = {};
      data.items.forEach(t => { d[t.type] = { normal: t.normal_price, promo: t.promo_price }; });
      setDrafts(d);
    });

  useEffect(() => {
    load();
    api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  async function toggleCalculator() {
    if (!settings) return;
    const next = !settings.pricing_calculator_enabled;
    setTogglingCalc(true);
    try {
      const { data } = await api.patch("/admin/settings/pricing-calculator", { enabled: next });
      setSettings(data);
      toast.success(next ? "Calculadora ativada." : "Calculadora desativada.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTogglingCalc(false); }
  }

  function setDraft(type, field, raw) {
    const n = parseFloat(raw);
    setDrafts(d => ({ ...d, [type]: { ...d[type], [field]: Number.isFinite(n) ? n : 0 } }));
  }

  async function save(t) {
    const d = drafts[t.type];
    if (d.promo > d.normal) { toast.error("Preço promocional não pode ser maior que o normal."); return; }
    setSaving(t.type);
    try {
      await api.put(`/admin/apt-type-pricing/${t.type}`, { normal: d.normal, promo: d.promo });
      toast.success(`Preços de ${t.type} atualizados.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(null); }
  }

  async function reset(t) {
    if (!window.confirm(`Restaurar preços predefinidos de ${t.type}?`)) return;
    try {
      await api.delete(`/admin/apt-type-pricing/${t.type}`);
      toast.success(`${t.type} revertido aos preços originais.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-3">Catálogo</div>
        <h1 className="font-display text-5xl">Preços das tipologias.</h1>
        <div className="text-sm text-[#a0b2af] mt-2 max-w-2xl">
          Ajuste os preços base (normal / promoção) de cada tipologia. As alterações aplicam-se a novas reservas — reservas existentes mantêm o preço original.
        </div>
      </div>

      {/* Toggle: calculadora de preços pública */}
      <section
        className={`lux-card p-5 flex items-start gap-4 flex-wrap ${
          settings?.pricing_calculator_enabled
            ? "border-[#d4af37]/40"
            : "border-[#ef4444]/40 bg-[#ef4444]/5"
        }`}
        data-testid="pricing-calculator-toggle-card"
      >
        <div className="w-12 h-12 rounded-sm bg-[#d4af37]/10 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] shrink-0">
          <Calculator size={20} />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="eyebrow text-[#d4af37] mb-1">Calculadora de preços pública</div>
          <div className="text-sm text-[#fdfbf7]">
            Estado:{" "}
            <strong
              className={settings?.pricing_calculator_enabled ? "text-[#10b981]" : "text-[#fca5a5]"}
              data-testid="pricing-calculator-state"
            >
              {settings?.pricing_calculator_enabled ? "ATIVADA" : "DESATIVADA"}
            </strong>
          </div>
          <div className="text-xs text-[#a0b2af] mt-1">
            {settings?.pricing_calculator_enabled
              ? "A calculadora aparece na página pública /precos."
              : "A calculadora está escondida na página pública — visitantes não a veem."}
          </div>
          {settings?.pricing_calculator_toggled_at && (
            <div className="text-[10px] text-[#a0b2af] mt-1">
              Última alteração: {new Date(settings.pricing_calculator_toggled_at).toLocaleString("pt-PT")}
            </div>
          )}
        </div>
        <button
          onClick={toggleCalculator}
          disabled={togglingCalc || !settings}
          className={`px-4 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 rounded-sm transition-colors disabled:opacity-50 ${
            settings?.pricing_calculator_enabled
              ? "border border-[#ef4444]/60 text-[#fca5a5] hover:bg-[#ef4444]/10"
              : "btn-gold"
          }`}
          data-testid="pricing-calculator-toggle-btn"
          title={settings?.pricing_calculator_enabled ? "Desativar" : "Ativar"}
        >
          <Power size={14} />
          {togglingCalc
            ? "A guardar…"
            : settings?.pricing_calculator_enabled
            ? "Desativar"
            : "Ativar"}
        </button>
      </section>

      <div className="grid md:grid-cols-2 gap-4">
        {types.map(t => {
          const d = drafts[t.type] || {};
          const modified = d.normal !== t.normal_price || d.promo !== t.promo_price;
          return (
            <div key={t.type} className={`lux-card p-5 ${modified ? "border-[#d4af37]" : ""}`}
                 data-testid={`pricing-card-${t.type}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-display text-3xl">{t.type}</div>
                  <div className="text-[11px] text-[#a0b2af]">{t.label}</div>
                </div>
                <div className="text-right text-xs text-[#a0b2af] inline-flex items-center gap-1">
                  <Clock size={11} />
                  {Math.floor(t.duration / 60)}h{t.duration % 60 ? ` ${t.duration % 60}m` : ""} fixo
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Preço normal</label>
                  <div className="relative">
                    <input type="number" min="0" step="0.5" value={d.normal ?? ""}
                           onChange={e => setDraft(t.type, "normal", e.target.value)}
                           className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-3 py-2 pr-9 text-lg font-display focus:border-[#d4af37] outline-none"
                           data-testid={`price-normal-${t.type}`} />
                    <Euro size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#d4af37]" />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Preço promo</label>
                  <div className="relative">
                    <input type="number" min="0" step="0.5" value={d.promo ?? ""}
                           onChange={e => setDraft(t.type, "promo", e.target.value)}
                           className="w-full bg-[#062c27] border border-[#d4af37]/40 rounded-sm px-3 py-2 pr-9 text-lg font-display text-[#d4af37] focus:border-[#d4af37] outline-none"
                           data-testid={`price-promo-${t.type}`} />
                    <Euro size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#d4af37]" />
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div className="text-[10px] text-[#a0b2af]">
                  Atual {t.promo_active ? "promo" : "normal"}:
                  <span className="text-[#d4af37] font-mono ml-1">{t.current_price}€</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => reset(t)} title="Restaurar preço original"
                          className="p-2 border border-[#1a4640] rounded-sm text-[#a0b2af] hover:text-[#d4af37] hover:border-[#d4af37]"
                          data-testid={`reset-${t.type}`}>
                    <RotateCcw size={13} />
                  </button>
                  <button onClick={() => save(t)} disabled={!modified || saving === t.type}
                          className="btn-gold px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40"
                          data-testid={`save-${t.type}`}>
                    <Save size={12} /> {saving === t.type ? "A guardar…" : "Guardar"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
